import { QueryClient } from 'react-query'
import { DateTime } from 'luxon'
import { TEvent } from '../../utils/types'

/*
 * Calendar events the user has created that a server events list has not yet settled. They live
 * outside the ['events', ...] cache on purpose: event refetches replace that cache wholesale, and a
 * month may not have loaded yet, so a placement written there could be skipped or erased until the
 * server round trip (which waits on Google) finished. useEvents merges these into what it returns.
 */
export const PENDING_EVENT_CREATES_KEY = 'pending_event_creates'

export interface TPendingEventCreate {
    blockStartTime: string
    event: TEvent
    // the full create payload, so an exact repeat of an in-flight create is recognised
    payloadKey: string
    // set once the server has the event; the next events fetch that started after this settles it
    confirmedAt?: number
    // server events already on the calendar when this was placed, which can never be its early copy
    knownEventIds: string[]
}

export const getPendingCreates = (queryClient: QueryClient) =>
    queryClient.getQueryData<TPendingEventCreate[]>(PENDING_EVENT_CREATES_KEY) ?? []

const setPendingCreates = (
    queryClient: QueryClient,
    update: (pending: TPendingEventCreate[]) => TPendingEventCreate[]
) => queryClient.setQueryData(PENDING_EVENT_CREATES_KEY, update(getPendingCreates(queryClient)))

export const getPayloadKey = (payload: object) => JSON.stringify(payload)

/*
 * True while the same create, payload for payload, is still in flight. Two drops or clicks that differ
 * in anything (time, duration, calendar) are separate requests and both go through.
 */
export const isDuplicatePendingCreate = (queryClient: QueryClient, payload: object) => {
    const payloadKey = getPayloadKey(payload)
    return getPendingCreates(queryClient).some((pending) => !pending.confirmedAt && pending.payloadKey === payloadKey)
}

export const addPendingCreate = (
    queryClient: QueryClient,
    { blockStartTime, event, payload }: { blockStartTime: string; event: TEvent; payload: object }
) => {
    const knownEventIds = (queryClient.getQueryData<TEvent[]>(['events', 'calendar', blockStartTime]) ?? []).map(
        (e) => e.id
    )
    setPendingCreates(queryClient, (pending) => [
        ...pending,
        { blockStartTime, event, payloadKey: getPayloadKey(payload), knownEventIds },
    ])
}

// Server id -> the optimistic id the event was first rendered under, so confirming a create does not
// change its React key and remount it.
const renderKeyByEventId = new Map<string, string>()
export const getEventRenderKey = (event: TEvent) => event.optimisticId ?? renderKeyByEventId.get(event.id) ?? event.id

/** Adopts the server id. Returns the confirmed event, or undefined if the placement was already removed. */
export const confirmPendingCreate = (queryClient: QueryClient, optimisticId: string, id: string, now = Date.now()) => {
    renderKeyByEventId.set(id, optimisticId)
    const pending = getPendingCreates(queryClient).find((p) => p.event.id === optimisticId)
    if (!pending) return undefined
    const event = { ...pending.event, id, optimisticId: undefined }
    setPendingCreates(queryClient, (pendingCreates) =>
        pendingCreates.map((p) => (p === pending ? { ...p, event, confirmedAt: now } : p))
    )
    return event
}

/** Rolls back a create the server rejected. */
export const removePendingCreate = (queryClient: QueryClient, id: string) =>
    updatePendingCreate(queryClient, id, () => null)

/** Applies an optimistic move or resize (or, with null, a delete) to a create that is still pending. */
export const updatePendingCreate = (queryClient: QueryClient, id: string, update: (event: TEvent) => TEvent | null) => {
    if (!getPendingCreates(queryClient).some((pending) => pending.event.id === id)) return
    setPendingCreates(queryClient, (pendingCreates) =>
        pendingCreates.flatMap((pending) => {
            if (pending.event.id !== id) return [pending]
            const event = update(pending.event)
            return event ? [{ ...pending, event }] : []
        })
    )
}

/*
 * When the latest successful fetch of each calendar month started and landed. setQueryData also
 * moves a query's dataUpdatedAt, so this is how a real fetch is told apart from a local write.
 */
const lastEventsFetch = new Map<string, { startedAt: number; landedAt: number }>()
export const recordEventsFetch = (blockStartTime: string, startedAt: number, landedAt = Date.now()) =>
    lastEventsFetch.set(blockStartTime, { startedAt, landedAt })

/*
 * Once the cached events for a month come from a fetch that started after a create was confirmed, the
 * server list is the authority for it, whether or not it includes the event. A fetch already in flight
 * at confirmation may predate the event, so it settles nothing.
 */
export const settleConfirmedCreates = (queryClient: QueryClient, blockStartTime: string, dataUpdatedAt: number) => {
    const lastFetch = lastEventsFetch.get(blockStartTime)
    if (!lastFetch || dataUpdatedAt < lastFetch.landedAt) return
    const isSettled = (pending: TPendingEventCreate) =>
        pending.blockStartTime === blockStartTime &&
        pending.confirmedAt !== undefined &&
        pending.confirmedAt < lastFetch.startedAt
    if (!getPendingCreates(queryClient).some(isSettled)) return
    setPendingCreates(queryClient, (pending) => pending.filter((p) => !isSettled(p)))
}

const isSameInstant = (a: string, b: string) => DateTime.fromISO(a).toMillis() === DateTime.fromISO(b).toMillis()

/*
 * Whether the server list already holds this placement. Once confirmed that is an id match. Before then,
 * a fetch can land after Google has the event but before the create responds, so an unconfirmed placement
 * also matches a server event for the same task or list, account, calendar and span — as long as that
 * event is new: not on the calendar when this was placed, and not already another create's server copy.
 * Scheduling the same slot twice is allowed, so an older identical event must not hide the new one.
 */
const isEarlyServerCopy = (event: TEvent, pending: TPendingEventCreate) => {
    const placed = pending.event
    return (
        !pending.confirmedAt &&
        (placed.linked_task_id || placed.linked_view_id) &&
        !pending.knownEventIds.includes(event.id) &&
        !renderKeyByEventId.has(event.id) &&
        event.linked_task_id === placed.linked_task_id &&
        event.linked_view_id === placed.linked_view_id &&
        event.account_id === placed.account_id &&
        (!placed.calendar_id || event.calendar_id === placed.calendar_id) &&
        isSameInstant(event.datetime_start, placed.datetime_start) &&
        isSameInstant(event.datetime_end, placed.datetime_end)
    )
}
const isInServerList = (events: TEvent[], pending: TPendingEventCreate) =>
    events.some((event) => event.id === pending.event.id || isEarlyServerCopy(event, pending))

/*
 * The month's events plus its pending creates. Pending creates show before the month has loaded and skip
 * the selected-calendar filter (the user just placed them, and the task-to-cal calendar is kept selected
 * anyway); one the server list already has is not shown twice.
 */
export const mergePendingCreates = (
    events: TEvent[] | undefined,
    pendingCreates: TPendingEventCreate[]
): TEvent[] | undefined => {
    const pendingEvents = pendingCreates
        .filter((pending) => !events || !isInServerList(events, pending))
        .map((pending) => pending.event)
    if (pendingEvents.length === 0) return events
    return [...(events ?? []), ...pendingEvents]
}
